import { createContext, ReactNode, useContext } from 'react';

import { GettingStartedVisibility } from '@/models/GettingStarted';

import { BillingCycle, BillingPeriod } from '../components/CycleInfo/CycleInfo.types';
import { InsightsPlan } from '../hooks/useInsightsSubscription.types';

export type PagePropsContextType = {
  subscriptionDetails?: {
    planName: string;
    billingCycles?: BillingCycle[];
    billingPeriod?: BillingPeriod;
    privateOrDedicated: boolean;
    hasZeroMarginInfra?: boolean;
  };
  currentUser: {
    email: string;
    username: string;
  };
  currentAccount: {
    appsCount: number;
    creditBased: boolean;
    gettingStartedVisibility: GettingStartedVisibility;
    hasApps: boolean;
    hasProjects: boolean;
    plan: InsightsPlan;
    profile: {
      avatarUrl: string;
      isCreditBased: boolean;
      isMember: boolean;
      isModernAccount: boolean;
      isPlatformPricing: boolean;
      isOwner: boolean;
      name: string;
      paymentProcessor: string;
      slug: string;
      type: string;
      typeName: string;
    };
    showEstimatedCreditUsage: boolean;
    showCiVcpuUtilization: boolean;
    showBuildHub: boolean;
    showRbe: boolean;
    showRde: boolean;
    slug: string;
  };
  detailsId: string;
  traceToken: string;
  workspaceOwner: boolean;
};

type PagePropsContextProviderProps = {
  children: ReactNode;
} & PagePropsContextType;

export const initialState: PagePropsContextType = {
  subscriptionDetails: { planName: '', privateOrDedicated: false },
  currentUser: {
    email: '',
    username: '',
  },
  currentAccount: {
    appsCount: 0,
    creditBased: false,
    gettingStartedVisibility: 'none' as GettingStartedVisibility,
    hasApps: false,
    hasProjects: false,
    plan: {
      last30DaysUsagePct: 0,
      paying: false,
      nextRenewalAt: '',
      trialEndTime: '',
      workspaceBecome3MonthsOld: '',
    },
    profile: {
      avatarUrl: '',
      isCreditBased: false,
      isMember: false,
      isModernAccount: false,
      isPlatformPricing: false,
      isOwner: false,
      name: '',
      paymentProcessor: '',
      slug: '',
      type: '',
      typeName: '',
    },
    showEstimatedCreditUsage: false,
    showCiVcpuUtilization: false,
    showBuildHub: false,
    showRbe: false,
    showRde: false,
    slug: '',
  },
  detailsId: '',
  traceToken: '',
  workspaceOwner: false,
};

const PagePropsContext = createContext<PagePropsContextType>(initialState);

export const PagePropsContextProvider = ({ children, ...rest }: PagePropsContextProviderProps) => {
  return <PagePropsContext.Provider value={rest}>{children}</PagePropsContext.Provider>;
};

export const usePageProps = () => useContext(PagePropsContext);
